<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? "Editar horario" : "Nuevo horario" }}</h1>
    </div>

    <form @submit.prevent="save" novalidate>
      <wz-card :title="editing ? 'Datos del horario' : 'Datos del nuevo horario'">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-2/3 mb-3 px-2">
            <wz-input label="Nombre" required v-model="form.name" placeholder="Ej: Oficina estándar"
              :error="$v.form.name.$error && !$v.form.name.required ? 'El nombre es requerido' : ''" />
          </div>
          <div v-if="editing" class="w-full md:w-1/3 mb-3 px-2">
            <wz-input label="Código" :disabled="true" v-model="form.code" />
          </div>
        </div>

        <!-- Horario por día -->
        <div class="wz-form-group mb-3">
          <label class="wz-form-label">Horario por día <span class="wz-form-required">*</span></label>
          <div class="wz-table-responsive">
            <table class="wz-table wz-table--bordered" style="min-width: 760px;">
              <thead>
                <tr>
                  <th style="width: 110px;">Día</th>
                  <th style="width: 80px; text-align: center;">Labora</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>Inicio descanso</th>
                  <th>Fin descanso</th>
                  <th style="width: 120px;">Horas efectivas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.key" :class="{ 'wz-day-off': !row.working }">
                  <td><strong>{{ row.label }}</strong></td>
                  <td style="text-align: center;">
                    <wz-checkbox switch v-model="row.working" @change="onToggle(row)" />
                  </td>
                  <td>
                    <wz-input type="time" v-model="row.start" :disabled="!row.working"
                      :error="row.errStart" />
                  </td>
                  <td>
                    <wz-input type="time" v-model="row.end" :disabled="!row.working"
                      :error="row.errEnd" />
                  </td>
                  <td>
                    <wz-input type="time" v-model="row.breakStart" :disabled="!row.working" />
                  </td>
                  <td>
                    <wz-input type="time" v-model="row.breakEnd" :disabled="!row.working" />
                  </td>
                  <td>
                    <span v-if="row.working && effectiveOf(row)">{{ effectiveOf(row) }} h</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <span v-if="gridError" class="wz-form-error-msg mt-1">
            <i class="ri-error-warning-line"></i>{{ gridError }}
          </span>
        </div>

        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-input label="Tolerancia entrada (min)" type="number" v-model.number="form.toleranceMinutes" min="0" placeholder="0" />
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <wz-button type="button" variant="light" @click="$router.push({ name: 'work-schedule-list' })">{{ editing ? "Volver" : "Cancelar" }}</wz-button>
          <wz-button type="submit" variant="primary" class="ml-2">Guardar</wz-button>
        </div>
      </wz-card>
    </form>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required } from 'vuelidate/lib/validators'
import { WorkScheduleService } from '@/core/services'

const DAYS = [
  { key: 1, name: 'monday',    label: 'Lunes' },
  { key: 2, name: 'tuesday',   label: 'Martes' },
  { key: 3, name: 'wednesday', label: 'Miércoles' },
  { key: 4, name: 'thursday',  label: 'Jueves' },
  { key: 5, name: 'friday',    label: 'Viernes' },
  { key: 6, name: 'saturday',  label: 'Sábado' },
  { key: 7, name: 'sunday',    label: 'Domingo' },
]

const newRow = (d, working) => ({
  ...d, working, start: null, end: null, breakStart: null, breakEnd: null, errStart: '', errEnd: '',
})

export default {
  data() {
    return {
      editing: false,
      gridError: '',
      rows: DAYS.map(d => newRow(d, d.key <= 5)), // lun-vie laborable por defecto
      form: { name: null, code: null, toleranceMinutes: 0 },
    }
  },
  validations() {
    return {
      form: { name: { required } },
    }
  },
  watch: {
    '$route.params.id'() { if (this.$route.params.id) this.load(this.$route.params.id) },
  },
  methods: {
    onToggle(row) {
      if (!row.working) {
        row.start = null; row.end = null; row.breakStart = null; row.breakEnd = null
        row.errStart = ''; row.errEnd = ''
      }
    },
    effectiveOf(row) {
      if (!row.start || !row.end) return ''
      const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m }
      let mins = toMin(row.end) - toMin(row.start)
      if (mins < 0) mins += 24 * 60
      if (row.breakStart && row.breakEnd) {
        let b = toMin(row.breakEnd) - toMin(row.breakStart)
        if (b < 0) b += 24 * 60
        mins -= b
      }
      if (mins <= 0) return ''
      return parseFloat((mins / 60).toFixed(2))
    },
    validateGrid() {
      let ok = true
      this.rows.forEach(r => {
        r.errStart = ''; r.errEnd = ''
        if (!r.working) return
        if (!r.start) { r.errStart = 'Requerida'; ok = false }
        if (!r.end) { r.errEnd = 'Requerida'; ok = false }
        if (r.start && r.end && r.end <= r.start) { r.errEnd = 'Debe ser mayor a la entrada'; ok = false }
      })
      const anyWorking = this.rows.some(r => r.working)
      if (!anyWorking) { this.gridError = 'Debe marcar al menos un día laborable'; ok = false }
      else if (!ok) { this.gridError = 'Complete las horas de los días laborables' }
      else { this.gridError = '' }
      return ok
    },
    async save() {
      this.$v.$touch()
      const gridOk = this.validateGrid()
      if (this.$v.$error || !gridOk) {
        this.$nextTick(() => { const el = document.querySelector('.wz-form-error-msg'); if (el) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 }) })
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const data = { ...this.form }
        this.rows.forEach(r => {
          data[`${r.name}Start`]      = r.working ? r.start : null
          data[`${r.name}End`]        = r.working ? r.end : null
          data[`${r.name}BreakStart`] = r.working ? r.breakStart : null
          data[`${r.name}BreakEnd`]   = r.working ? r.breakEnd : null
        })
        if (!this.editing) {
          const resp = await WorkScheduleService.save(data)
          Alert.success('Se almacenó la información correctamente')
          this.$router.push({ name: 'work-schedule-edit', params: { id: resp.payload.id } })
          this.editing = true
        } else {
          await WorkScheduleService.update(this.$route.params.id, data)
          Alert.success('Se almacenó la información correctamente')
        }
      } catch (ex) { console.error(ex); Alert.error(ex.errors ? ex.errors.message : 'Error al guardar') }
      finally { this.$store.dispatch('app/loading', false) }
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const payload = (await WorkScheduleService.get(id)).payload
        this.form = { name: payload.name, code: payload.code, toleranceMinutes: payload.toleranceMinutes || 0 }

        this.rows = DAYS.map(d => {
          const start = payload[`${d.name}Start`] || null
          const end   = payload[`${d.name}End`] || null
          return {
            ...d,
            working: !!(start && end),
            start,
            end,
            breakStart: payload[`${d.name}BreakStart`] || null,
            breakEnd:   payload[`${d.name}BreakEnd`] || null,
            errStart: '', errEnd: '',
          }
        })

        // Fallback: horario antiguo sin datos por día → rellenar desde los globales
        const anyPerDay = this.rows.some(r => r.start || r.end)
        if (!anyPerDay && payload.workDaysList && payload.workDaysList.length) {
          const workDays = new Set(payload.workDaysList)
          this.rows.forEach(r => {
            r.working = workDays.has(r.key)
            if (r.working) {
              r.start = payload.entryTime || null
              r.end = payload.exitTime || null
              r.breakStart = payload.lunchStart || null
              r.breakEnd = payload.lunchEnd || null
            }
          })
        }
        this.editing = true
      } catch (e) { console.error(e); Alert.error(e.errors ? e.errors.message : 'Error al cargar'); this.$router.push({ name: 'work-schedule-list' }) }
      finally { this.$store.dispatch('app/loading', false) }
    },
  },
  async created() { if (this.$route.params.id) await this.load(this.$route.params.id) },
}
</script>

<style lang="stylus" scoped>
.wz-table-responsive
  overflow-x auto
.wz-day-off td
  opacity 0.55
</style>
